// @ts-nocheck
import React, { FC, memo } from 'react'
import { DateTime } from 'luxon'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  ArcElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Chart,
  Plugin,
  TooltipItem
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import 'chartjs-adapter-moment'
import 'chartjs-adapter-dayjs-4/dist/chartjs-adapter-dayjs-4.esm'

import {
  formatCurrencyShort,
  formatNumericValue
} from '../utils/housing-market'
import { DataPoint } from '@libs/types/generated/housing-market-ui'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
)

ChartJS.register({
  id: 'hoverVerticalLine',
  afterDraw: function (chart: any) {
    if (chart.tooltip._active && chart.tooltip._active.length) {
      const activePoint = chart.tooltip._active[0]
      const ctx = chart.ctx
      const x = activePoint.element.x
      const y = activePoint.element.y
      const bottomY = chart.scales.y.bottom
      ctx.save()

      ctx.beginPath()
      ctx.moveTo(x, y)
      ctx.lineTo(x, bottomY)
      ctx.lineWidth = 2
      ctx.strokeStyle = '#0137A1'
      ctx.stroke()
      ctx.closePath()

      ctx.beginPath()
      ctx.arc(x, y, 4, Math.PI / 180, false)
      ctx.closePath()
      ctx.fillStyle = '#007871'
      ctx.fill()

      ctx.restore()
    }
  }
})

Tooltip.positioners.nearest = function (eventPosition) {
  const {
    scales: { x, y }
  } = this.chart

  const topY = this.chart.scales.y.top
  const bottomY = this.chart.scales.y.bottom
  const topX = this.chart.scales.x.top

  if (eventPosition.length === 0) {
    return { x: topX, y: bottomY }
  }

  const xPosition = eventPosition[0]?.element?.x || 0
  const yPosition = eventPosition[0]?.element?.y || 0

  const xValue = x.getValueForPixel(xPosition) || 0
  const yValue = y.getValueForPixel(yPosition) || 0

  return {
    x: x.getPixelForValue(xValue),
    y: y.getPixelForValue(yValue) + (topY - bottomY) * 0.1,
    xAlign: 'center',
    yAlign: 'bottom'
  }
}

interface Props {
  chartId: string
  format?: 'currency'
  dataPoints: DataPoint[]
  chartHeight: number
}

const getOrCreateLegendList = (id: string) => {
  const legendContainer = document.getElementById(id)
  let listContainer = legendContainer?.querySelector('ul')

  if (!listContainer && legendContainer) {
    listContainer = document.createElement('ul')
    listContainer.style.display = 'flex'
    listContainer.style.flexDirection = 'row'
    listContainer.style.margin = '0'
    listContainer.style.padding = '0'

    legendContainer.appendChild(listContainer)
  }

  return listContainer
}

const htmlLegendPlugin: Plugin<'line'> = {
  id: 'htmlLegend',
  afterUpdate(chart, args, options) {
    const ul = getOrCreateLegendList(options.containerID)

    if (!ul) return

    while (ul.firstChild) {
      ul.firstChild.remove()
    }

    const items = chart.options.plugins?.legend?.labels?.generateLabels?.(chart)

    const dataSetTexts = chart.legend?.legendItems?.map(
      (dataset: { text: string }) => {
        return dataset.text
      }
    )

    if (!items || !dataSetTexts) return

    items.forEach(item => {
      const index = dataSetTexts.indexOf(item.text)
      const datasetVisible = chart.isDatasetVisible(index)

      const li = document.createElement('li')
      li.style.alignItems = 'center'
      li.style.cursor = 'pointer'
      li.style.display = 'flex'
      li.style.flexDirection = 'row'
      li.style.marginLeft = '12px'
      li.style.textUnderlineOffset = '5px'

      li.onclick = () => {
        if (!datasetVisible) {
          for (let i = 0; i < dataSetTexts.length; i++) {
            if (i !== index) {
              chart.hide(i)
            } else {
              chart.show(i)
            }
          }
        }

        chart.update()
      }

      // Text
      const textContainer = document.createElement('p')
      textContainer.style.fontFamily = 'Helvetica Neue'
      textContainer.style.fontWeight = datasetVisible ? 'bold' : ''
      textContainer.style.fontSize = '14px'
      textContainer.style.color = item.fontColor as string
      textContainer.style.margin = '0'
      textContainer.style.padding = '10'
      textContainer.style.textDecoration = datasetVisible
        ? 'underline solid 2px'
        : ''

      const text = document.createTextNode(item.text)
      textContainer.appendChild(text)

      li.appendChild(textContainer)
      ul.appendChild(li)
    })
  }
}

const MarketChart: FC<Props> = ({
  chartId,
  dataPoints,
  chartHeight,
  format
}) => {
  if (dataPoints.length === 0) {
    return ''
  }

  const roundedDataPoints = dataPoints.map(dataPoint => {
    return {
      timestamp: dataPoint.timestamp,
      value: Math.round(dataPoint.value)
    }
  })

  const dataPoints5y = roundedDataPoints.slice(0, 61)
  const dataPoints3y = roundedDataPoints.slice(0, 37)
  const dataPoints1y = roundedDataPoints.slice(0, 13)

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { delay: 200 },
    tension: 0.3,
    plugins: {
      htmlLegend: {
        containerID: 'legend-container'
      },
      legend: {
        display: false,
        labels: {
          generateLabels: (chart: any) => {
            return chart.data.datasets.map((dataset: any, index: number) => ({
              text: dataset.label,
              fillStyle: chart.isDatasetVisible(index) ? '#000000' : '#868A9A',
              fontColor: chart.isDatasetVisible(index) ? '#000000' : '#868A9A'
            }))
          }
        }
      },
      tooltip: {
        intersect: false,
        mode: 'index',
        displayColors: false,
        backgroundColor: '#fff',
        cornerRadius: 8,
        caretSize: 0,
        borderColor: '#E3E6EE',
        borderWidth: 2,
        titleColor: '#868A9A',
        titleAlign: 'center',
        titleFont: {
          family: 'Helvetica Neue',
          size: 12,
          weight: 'normal'
        },
        bodyColor: '#000000',
        bodyFont: {
          family: 'Helvetica Neue',
          size: 14,
          weight: 'bold'
        },
        bodyAlign: 'center',
        animate: false,
        position: 'nearest',
        callbacks: {
          title: (context: TooltipItem<'line'>[]) => {
            const formattedDate = DateTime.fromISO(context[0].raw.x, {
              zone: 'utc'
            }).toFormat('LLL yyyy')

            return formattedDate
          },
          label: context => {
            const value = context.formattedValue

            if (format === 'currency') {
              return `$${value}`
            }

            return value
          }
        }
      }
    },
    elements: {
      point: {
        radius: 0,
        hitRadius: 0,
        backgroundColor: '#868A9A'
      }
    },
    scales: {
      x: {
        grid: {
          offset: false,
          drawOnChartArea: false
        },
        offset: false,
        ticks: {
          font: 12,
          align: 'center'
        },
        type: 'time',
        time: {
          unit: 'month',
          displayFormats: {
            month: 'MMM YYYY'
          }
        },
        afterTickToLabelConversion: ctx => {
          const TickToLabel = ctx.ticks
          TickToLabel.font = 12
          ctx.ticks = []
          TickToLabel.forEach(data => {
            const lowercaseLabel = data.label.toLowerCase()
            if (
              lowercaseLabel.startsWith('mar') ||
              lowercaseLabel.startsWith('jun') ||
              lowercaseLabel.startsWith('sep') ||
              lowercaseLabel.startsWith('dec')
            ) {
              ctx.ticks.push(data)
            }
          })
        }
      },
      y: {
        type: 'linear',
        display: true,
        position: 'right',
        grace: '40%',
        grid: {
          drawOnChartArea: true
        },
        ticks: {
          callback: value => {
            if (format === 'currency') {
              return formatCurrencyShort(value)
            }

            return formatNumericValue(value)
          },
          font: 12,
          padding: 10
        }
      }
    },
    layout: {
      padding: {
        top: 2,
        left: 10,
        right: 8,
        bottom: 10
      }
    }
  }

  const data = {
    datasets: [
      {
        label: '1 year',
        data: dataPoints1y.map(row => ({ x: row.timestamp, y: row.value })),
        borderColor: '#007871',
        borderWidth: 2,
        hidden: false
      },
      {
        label: '3 year',
        data: dataPoints3y.map(row => ({ x: row.timestamp, y: row.value })),
        borderColor: '#007871',
        borderWidth: 2,
        hidden: true
      },
      {
        label: '5 year',
        data: dataPoints5y.map(row => ({ x: row.timestamp, y: row.value })),
        borderColor: '#007871',
        borderWidth: 2,
        hidden: true
      }
    ]
  }

  return (
    <div
      className="mx-2 my-2 px-1 pt-2 pb-12"
      style={{
        height: `${chartHeight}px`,
        border: 'solid 4px #E3E6EE',
        borderRadius: '12px'
      }}
    >
      <div id={chartId} className="float-right py-2 mr-4"></div>
      <Line
        options={{
          ...options,
          plugins: {
            ...options.plugins,
            htmlLegend: { ...options.plugins.htmlLegend, containerID: chartId }
          }
        }}
        data={data}
        plugins={[htmlLegendPlugin]}
        redraw={true}
      />
    </div>
  )
}

export default memo(MarketChart)
